<!-- =========================================================================
     STATE 1: CHECKING PERMISSION (LOADING)
     ========================================================================= -->
<div *ngIf="isCheckingAccess" class="access-check-loading-container">
  <div class="access-loading-card">
    <div class="loading-spinner-wrap">
      <i class="fa fa-circle-o-notch fa-spin"></i>
    </div>
    <h3>Verifying Access Permission</h3>
    <p>Please wait while we verify your access to Chat with Users for this organization...</p>
  </div>
</div>

<!-- =========================================================================
     STATE 2: ACCESS DENIED / NO ACCESS
     ========================================================================= -->
<div *ngIf="!isCheckingAccess && !hasAccess" class="no-access-screen-wrapper">
  <div class="no-access-card">

    <!-- Icon Badge -->
    <div class="no-access-icon-badge" [ngClass]="accessDenialReason">
      <i *ngIf="accessDenialReason === 'not_logged_in'" class="fa fa-lock"></i>
      <i *ngIf="accessDenialReason === 'email_mismatch'" class="fa fa-user-times"></i>
      <i *ngIf="accessDenialReason === 'org_mismatch'" class="fa fa-building-o"></i>
      <i *ngIf="accessDenialReason === 'not_invited' || accessDenialReason === 'error'" class="fa fa-shield"></i>
    </div>

    <!-- Title -->
    <h2 class="no-access-title">
      <span *ngIf="accessDenialReason === 'not_logged_in'">Sign-In Required</span>
      <span *ngIf="accessDenialReason === 'email_mismatch'">Different Account Logged In</span>
      <span *ngIf="accessDenialReason === 'org_mismatch'">No Access for Current Organization</span>
      <span *ngIf="accessDenialReason === 'not_invited'">Access Not Granted</span>
      <span *ngIf="accessDenialReason === 'error'">Verification Error</span>
    </h2>

    <!-- Explanation Message -->
    <p class="no-access-message">{{ accessDenialMessage }}</p>

    <!-- Context Details Box -->
    <div class="no-access-details-box">
      <div class="detail-row">
        <span class="detail-label"><i class="fa fa-user"></i> Logged-in User:</span>
        <span class="detail-val">{{ currentUserEmail || 'Not Signed In' }}</span>
      </div>
      <div class="detail-row">
        <span class="detail-label"><i class="fa fa-building"></i> Active Organization:</span>
        <span class="detail-val">{{ currentOrgName }} (Org ID: {{ currentOrgId }})</span>
      </div>
      <div *ngIf="invitedEmailFromUrl" class="detail-row highlight-row">
        <span class="detail-label"><i class="fa fa-envelope"></i> Invited Email:</span>
        <span class="detail-val highlight">{{ invitedEmailFromUrl }}</span>
      </div>
    </div>

    <!-- Authorized Organizations List (When org mismatch) -->
    <div *ngIf="accessDenialReason === 'org_mismatch' && authorizedOrgs && authorizedOrgs.length > 0" class="authorized-orgs-box">
      <div class="authorized-orgs-header">
        <i class="fa fa-check-circle" style="color: #059669;"></i>
        <span>Organizations where this email has Chat with Users access:</span>
      </div>
      <p class="authorized-orgs-hint">
        Please switch to one of the following organizations in your top navigation to access support chat:
      </p>
      <ul class="authorized-orgs-list">
        <li *ngFor="let org of authorizedOrgs">
          <div class="org-item-left">
            <i class="fa fa-building"></i>
            <span class="org-item-name">{{ org.org_name }}</span>
          </div>
          <span class="org-item-badge">Org #{{ org.organization_id }}</span>
        </li>
      </ul>
    </div>

    <!-- Action Buttons -->
    <div class="no-access-actions">
      <button *ngIf="accessDenialReason === 'not_logged_in'" type="button" class="btn-access-primary" (click)="goToLogin()">
        <i class="fa fa-sign-in"></i>
        <span>Sign In to Certiplate</span>
      </button>

      <button *ngIf="accessDenialReason !== 'not_logged_in'" type="button" class="btn-access-primary" (click)="goToDashboard()">
        <i class="fa fa-arrow-left"></i>
        <span>Go to Dashboard</span>
      </button>

      <button type="button" class="btn-access-secondary" (click)="checkPageAccess()">
        <i class="fa fa-refresh"></i>
        <span>Re-check Permission</span>
      </button>
    </div>

  </div>
</div>

<!-- =========================================================================
     STATE 3: AUTHORIZED CHAT DASHBOARD
     ========================================================================= -->
<ng-container *ngIf="!isCheckingAccess && hasAccess">
<div class="chat-page-wrapper">

  <!-- =========================================================================
       1. TOP BREADCRUMB & TOOLBAR (Matching Ticket Component Header)
       ========================================================================= -->
  <div class="ticket-breadcrumb-wrapper ticket-breadcrumb-flex">
    <div class="breadcrumb-left-side">
      <app-breadcrumb></app-breadcrumb>
    </div>

    <div class="breadcrumb-right-actions">
      <!-- Search Bar -->
      <form class="fd-top-search-form" autocomplete="off" role="search" (submit)="$event.preventDefault()" style="margin: 0; display: inline-flex;">
        <div class="fd-top-search-wrapper">
          <i class="fa fa-search fd-top-search-icon"></i>
          <input 
            type="search" 
            name="topSearchChatsInput"
            id="topSearchChatsInput"
            autocomplete="off"
            autocorrect="off"
            spellcheck="false" 
            class="fd-top-search-input" 
            placeholder="Search by user name, email, org, id..." 
            [(ngModel)]="searchQuery" />
          <span *ngIf="searchQuery" class="fd-clear-search-btn" (click)="searchQuery = ''">&times;</span>
        </div>
      </form>

      <!-- Refresh Button -->
      <button 
        type="button" 
        class="fd-btn-icon" 
        (click)="loadConversations(false)" 
        [disabled]="isLoadingConversations"
        title="Refresh conversations">
        <i class="fa fa-refresh" [class.fa-spin]="isLoadingConversations"></i>
      </button>

      <!-- Invite Support Member Button -->
      <button 
        type="button" 
        class="fd-btn fd-btn-send-invite" 
        (click)="openSendInviteModal()"
        title="Invite a team member to handle and reply to user chats">
        <i class="fa fa-user-plus"></i>
        <span>Invite Support Member</span>
      </button>
    </div>
  </div>

  <!-- =========================================================================
       2. VIEW A: TICKET-STYLE LIST VIEW (Shown when no conversation is opened)
       ========================================================================= -->
  <div *ngIf="!selectedConversation" class="chat-list-view-container">

    <!-- Filter Pills Row -->
    <div class="fd-filter-pills-row">
      <button 
        type="button" 
        class="fd-filter-pill" 
        [class.active]="activeFilter === 'all'" 
        (click)="setFilter('all')">
        All Chats ({{ conversations.length }})
      </button>
      <button 
        type="button" 
        class="fd-filter-pill" 
        [class.active]="activeFilter === 'unread'" 
        (click)="setFilter('unread')">
        Unread ({{ unreadCount }})
      </button>
      <button 
        type="button" 
        class="fd-filter-pill" 
        [class.active]="activeFilter === 'guest'" 
        (click)="setFilter('guest')">
        Guests ({{ guestCount }})
      </button>
      <button 
        type="button" 
        class="fd-filter-pill" 
        [class.active]="activeFilter === 'registered'" 
        (click)="setFilter('registered')">
        Registered Users ({{ registeredCount }})
      </button>
    </div>

    <!-- Loading State -->
    <div *ngIf="isLoadingConversations && conversations.length === 0" class="fd-loading-state">
      <i class="fa fa-circle-o-notch fa-spin fd-loading-spinner"></i>
      <span>Loading user chats...</span>
    </div>

    <!-- Empty State -->
    <div *ngIf="!isLoadingConversations && filteredConversations.length === 0" class="fd-empty-state">
      <div class="fd-empty-icon">
        <i class="fa fa-comments-o"></i>
      </div>
      <h3 class="fd-empty-title">No User Chats Found</h3>
      <p *ngIf="searchQuery" class="fd-empty-subtitle">No chats match "{{ searchQuery }}"</p>
      <p *ngIf="!searchQuery" class="fd-empty-subtitle">When users initiate a support chat, their threads will appear here.</p>
    </div>

    <!-- Ticket-Style Card List (Exact fd-ticket-card layout) -->
    <div *ngIf="filteredConversations.length > 0" class="fd-ticket-list-cards">
      <div 
        *ngFor="let conv of filteredConversations; trackBy: trackByConv" 
        class="fd-ticket-card"
        [class.unread-card]="conv.has_unread"
        (click)="selectConversation(conv)">
        
        <!-- Left Column: Colored Avatar Circle -->
        <div class="fd-card-left">
          <div class="fd-avatar" [style.background-color]="getAvatarColor(conv.user_name || conv.user_id)">
            {{ getInitials(conv.user_name) }}
          </div>
        </div>

        <!-- Center Column: Badges, Name, Meta -->
        <div class="fd-card-center">
          
          <!-- Badges Row -->
          <div class="fd-badges-row">
            <span *ngIf="conv.has_unread" class="fd-badge fd-badge-unread">
              <span class="unread-pulse-dot"></span> New Message
            </span>
            <span class="fd-badge fd-badge-tag">user_id: #{{ conv.user_id }}</span>
            <span *ngIf="conv.org_id" class="fd-badge fd-badge-tag">org_id: {{ conv.org_id }}</span>
            <span class="fd-badge fd-badge-tag">org: {{ conv.org_name || 'Certiplate' }}</span>
            <span *ngIf="conv.role === 'Guest' || conv.org_id === 0 || conv.user_id >= 9000000" class="fd-badge fd-badge-guest">Guest</span>
            <span *ngIf="conv.role && conv.role !== 'Guest' && conv.org_id !== 0 && conv.user_id < 9000000" class="fd-badge fd-badge-role">{{ conv.role }}</span>
          </div>

          <!-- User Name Title -->
          <div class="fd-ticket-title">
            <span class="fd-subject-text">{{ conv.user_name }}</span>
            <span class="fd-ticket-id">#{{ conv.user_id }}</span>
          </div>

          <!-- Meta Line -->
          <div class="fd-ticket-meta">
            <span *ngIf="conv.user_email && conv.user_email !== '—'" class="fd-meta-item">
              <i class="fa fa-envelope-o fd-meta-icon"></i>
              <span class="fd-meta-name">{{ conv.user_email }}</span>
            </span>
            <span *ngIf="conv.phone && conv.phone !== '—'" class="fd-meta-item">
              <i class="fa fa-phone fd-meta-icon"></i>
              <span class="fd-meta-name">{{ conv.phone }}</span>
            </span>
            <span class="fd-meta-dot">&middot;</span>
            <span class="fd-meta-item fd-meta-msg">
              <i class="fa fa-comment-o fd-meta-icon"></i>
              <span class="fd-meta-text">{{ conv.last_message || 'No messages yet' }}</span>
            </span>
          </div>

        </div>

        <!-- Right Column: Time & Actions -->
        <div class="fd-card-right">
          <span class="fd-meta-time">
            <i class="fa fa-clock-o"></i> {{ formatTime(conv.last_message_time) || 'Recently' }}
          </span>

          <div class="fd-card-action-group">
            <button 
              type="button" 
              class="fd-btn-toggle-unread"
              [class.active-unread]="conv.has_unread"
              [title]="conv.has_unread ? 'Mark as read' : 'Mark as unread'"
              (click)="toggleMarkUnread(conv, $event)">
              <i class="fa" [ngClass]="conv.has_unread ? 'fa-envelope-open-o' : 'fa-envelope-o'"></i>
              <span>{{ conv.has_unread ? 'Mark Read' : 'Mark Unread' }}</span>
            </button>

            <button 
              type="button" 
              class="fd-btn-open-chat"
              (click)="selectConversation(conv); $event.stopPropagation()">
              <i class="fa fa-comments"></i>
              <span>Open Chat</span>
            </button>
          </div>
        </div>

      </div>
    </div>

  </div>

  <!-- =========================================================================
       3. VIEW B: DETAIL CHAT VIEW (Shown when a user conversation is opened)
       ========================================================================= -->
  <div *ngIf="selectedConversation" class="chat-detail-view-container">

    <!-- Top Action Navigation Bar -->
    <div class="detail-nav-bar">
      <button 
        type="button" 
        class="fd-btn-back" 
        (click)="backToConversationsList()"
        title="Back to all user chats">
        <i class="fa fa-arrow-left"></i>
        <span>Back to User Chats</span>
      </button>

      <div class="detail-nav-actions">
        <!-- Mark as Unread Button -->
        <button 
          type="button" 
          class="detail-action-btn" 
          (click)="toggleCurrentChatUnread()" 
          [title]="selectedConversation.has_unread ? 'Mark as read' : 'Mark as unread'">
          <i class="fa" [ngClass]="selectedConversation.has_unread ? 'fa-envelope-open-o' : 'fa-envelope-o'"></i>
          <span>{{ selectedConversation.has_unread ? 'Mark Read' : 'Mark Unread' }}</span>
        </button>

        <!-- Refresh current chat thread -->
        <button 
          type="button" 
          class="detail-action-btn" 
          (click)="loadMessages(selectedConversation.user_id, selectedConversation.org_id)" 
          [disabled]="isLoadingMessages"
          title="Refresh messages">
          <i class="fa fa-refresh" [class.fa-spin]="isLoadingMessages"></i>
          <span>Refresh</span>
        </button>

        <!-- Invite Support Member Button in Detail View -->
        <button 
          type="button" 
          class="detail-action-btn" 
          (click)="openSendInviteModal()" 
          title="Invite a team member to handle and reply to user chats">
          <i class="fa fa-user-plus"></i>
          <span>Invite Support</span>
        </button>

        <!-- Toggle User Profile Details Panel -->
        <button 
          type="button" 
          class="detail-action-btn" 
          [class.active]="showUserDetailsPanel"
          (click)="toggleUserDetails()" 
          title="Toggle user details panel">
          <i class="fa fa-id-card-o"></i>
          <span>User Details</span>
        </button>
      </div>
    </div>

    <!-- Chat Box Card (Split between messages and optional side drawer) -->
    <div class="chat-detail-layout-card">

      <!-- Main Messages Stream & Composer Column -->
      <div class="chat-thread-column">

        <!-- Active Conversation Header Banner -->
        <div class="thread-user-header">
          <div class="header-user-avatar" [style.background-color]="getAvatarColor(selectedConversation.user_name || selectedConversation.user_id)">
            {{ getInitials(selectedConversation.user_name) }}
          </div>

          <div class="header-user-meta">
            <div class="name-row">
              <h3 class="user-display-name">{{ selectedConversation.user_name }}</h3>
              <span *ngIf="selectedConversation.role === 'Guest' || selectedConversation.org_id === 0 || selectedConversation.user_id >= 9000000" class="fd-badge fd-badge-guest">Guest</span>
              <span *ngIf="selectedConversation.role && selectedConversation.role !== 'Guest' && selectedConversation.org_id !== 0 && selectedConversation.user_id < 9000000" class="fd-badge fd-badge-role">{{ selectedConversation.role }}</span>
              <span class="user-id-tag">#{{ selectedConversation.user_id }}</span>
            </div>

            <div class="meta-row">
              <span class="meta-pill"><i class="fa fa-building-o"></i> {{ selectedConversation.org_name || 'Certiplate' }}</span>
              <span *ngIf="selectedConversation.user_email && selectedConversation.user_email !== '—'" class="meta-pill email-pill">
                <i class="fa fa-envelope-o"></i> {{ selectedConversation.user_email }}
              </span>
              <span *ngIf="selectedConversation.phone && selectedConversation.phone !== '—'" class="meta-pill">
                <i class="fa fa-phone"></i> {{ selectedConversation.phone }}
              </span>
            </div>
          </div>
        </div>

        <!-- Messages History Stream -->
        <div class="chat-messages-stream" #chatScrollContainer>
          
          <!-- Loading Spinner -->
          <div *ngIf="isLoadingMessages && messages.length === 0" class="messages-loading">
            <i class="fa fa-circle-o-notch fa-spin"></i>
            <span>Loading message thread...</span>
          </div>

          <!-- Empty Messages -->
          <div *ngIf="!isLoadingMessages && messages.length === 0" class="messages-empty">
            <i class="fa fa-commenting-o empty-thread-icon"></i>
            <h4>No messages in this conversation</h4>
            <p>Type a reply below to send a message to {{ selectedConversation.user_name }}.</p>
          </div>

          <!-- Bubbles -->
          <ng-container *ngIf="messages.length > 0">
            <ng-container *ngFor="let msg of messages; let i = index; trackBy: trackByMsg">
              
              <!-- Date Divider -->
              <div *ngIf="isFirstMessageOfDay(i)" class="date-divider">
                <span class="date-badge">{{ formatDateHeader(msg.created_at) }}</span>
              </div>

              <!-- Message Row -->
              <div 
                class="chat-bubble-row" 
                [class.incoming-bubble-row]="msg.sender === 'incoming'" 
                [class.outgoing-bubble-row]="msg.sender === 'outgoing'">

                <!-- User Avatar (for incoming user messages) -->
                <div *ngIf="msg.sender === 'incoming'" class="avatar-bubble user-avatar" [style.background-color]="getAvatarColor(msg.author_name || selectedConversation.user_name || selectedConversation.user_id)">
                  {{ getInitials(msg.author_name || selectedConversation.user_name) }}
                </div>

                <div class="bubble-content-block">
                  <div class="bubble-top-bar">
                    <div class="bubble-author-title">
                      {{ msg.sender === 'outgoing' ? 'Support Team (Admin)' : (msg.author_name || selectedConversation.user_name) }}
                    </div>

                    <!-- Delete Message Button (System Super Admin Only) -->
                    <button 
                      *ngIf="isSystemSuperAdmin" 
                      type="button"
                      class="btn-delete-msg" 
                      (click)="deleteMessage(msg, $event)" 
                      title="Delete message (System Super Admin only)">
                      <i class="fa fa-trash-o"></i>
                    </button>
                  </div>

                  <div class="message-bubble-card">
                    <p class="bubble-text">{{ msg.text }}</p>
                  </div>

                  <div class="bubble-footer-meta">
                    <span class="bubble-time">{{ msg.time || formatTimeOnly(msg.created_at) }}</span>
                    <i *ngIf="msg.sender === 'outgoing'" class="fa fa-check text-success"></i>
                  </div>
                </div>

                <!-- Admin Avatar (for outgoing admin messages) -->
                <div *ngIf="msg.sender === 'outgoing'" class="avatar-bubble admin-avatar">
                  <i class="fa fa-headphones"></i>
                </div>

              </div>

            </ng-container>
          </ng-container>

        </div>

        <!-- Chat Composer Box -->
        <div class="chat-composer-container">
          <div class="composer-sender-badge">
            <i class="fa fa-shield"></i>
            <span>Replying as <strong>{{ currentUserName }}</strong> (Admin Support)</span>
          </div>

          <div class="composer-input-row">
            <textarea 
              #messageTextarea
              class="composer-textarea-field"
              placeholder="Type your reply to {{ selectedConversation.user_name }}... (Press Enter to send, Shift+Enter for new line)"
              rows="2"
              [(ngModel)]="newMessageText"
              (keydown)="onKeyDown($event)"
              [disabled]="isSendingMessage">
            </textarea>

            <button 
              type="button" 
              class="composer-send-button" 
              [disabled]="!newMessageText.trim() || isSendingMessage"
              (click)="sendMessage()"
              title="Send reply">
              <i *ngIf="!isSendingMessage" class="fa fa-paper-plane"></i>
              <i *ngIf="isSendingMessage" class="fa fa-circle-o-notch fa-spin"></i>
              <span>Send</span>
            </button>
          </div>

          <div class="composer-hint-bar">
            <span><kbd>Enter</kbd> to send &bull; <kbd>Shift</kbd> + <kbd>Enter</kbd> for new line</span>
            <span *ngIf="newMessageText.length > 0">{{ newMessageText.length }} characters</span>
          </div>
        </div>

      </div>

      <!-- User Details Side Inspector (Collapsible) -->
      <aside *ngIf="showUserDetailsPanel" class="user-details-inspector-col">
        <div class="inspector-header">
          <h4 class="inspector-title">
            <i class="fa fa-user-circle-o"></i> User Profile
          </h4>
          <button type="button" class="inspector-close-btn" (click)="showUserDetailsPanel = false">&times;</button>
        </div>

        <div class="inspector-profile-card">
          <div class="profile-avatar-circle" [style.background-color]="getAvatarColor(selectedConversation.user_name || selectedConversation.user_id)">
            {{ getInitials(selectedConversation.user_name) }}
          </div>
          <h4 class="profile-name">{{ selectedConversation.user_name }}</h4>
          <span *ngIf="selectedConversation.role === 'Guest' || selectedConversation.org_id === 0 || selectedConversation.user_id >= 9000000" class="fd-badge fd-badge-guest">Guest</span>
          <span *ngIf="selectedConversation.role && selectedConversation.role !== 'Guest' && selectedConversation.org_id !== 0 && selectedConversation.user_id < 9000000" class="fd-badge fd-badge-role">{{ selectedConversation.role }}</span>
        </div>

        <div class="inspector-fields-list">
          <div class="field-item">
            <div class="field-title-line">
              <span class="field-label">User ID</span>
              <button type="button" class="field-copy-btn" (click)="copyToClipboard(selectedConversation.user_id.toString(), 'User ID')">
                <i class="fa fa-copy"></i> Copy
              </button>
            </div>
            <span class="field-value font-mono">#{{ selectedConversation.user_id }}</span>
          </div>

          <div class="field-item">
            <div class="field-title-line">
              <span class="field-label">Email Address</span>
              <button 
                *ngIf="selectedConversation.user_email && selectedConversation.user_email !== '—'" 
                type="button" 
                class="field-copy-btn" 
                (click)="copyToClipboard(selectedConversation.user_email, 'Email')">
                <i class="fa fa-copy"></i> Copy
              </button>
            </div>
            <span *ngIf="selectedConversation.user_email && selectedConversation.user_email !== '—'" class="field-value">
              <a [href]="'mailto:' + selectedConversation.user_email" class="mailto-link">{{ selectedConversation.user_email }}</a>
            </span>
            <span *ngIf="!selectedConversation.user_email || selectedConversation.user_email === '—'" class="field-value-empty">Not provided</span>
          </div>

          <div class="field-item">
            <div class="field-title-line">
              <span class="field-label">Phone Number</span>
              <button 
                *ngIf="selectedConversation.phone && selectedConversation.phone !== '—'" 
                type="button" 
                class="field-copy-btn" 
                (click)="copyToClipboard(selectedConversation.phone, 'Phone')">
                <i class="fa fa-copy"></i> Copy
              </button>
            </div>
            <span *ngIf="selectedConversation.phone && selectedConversation.phone !== '—'" class="field-value">
              <a [href]="'tel:' + selectedConversation.phone" class="mailto-link">{{ selectedConversation.phone }}</a>
            </span>
            <span *ngIf="!selectedConversation.phone || selectedConversation.phone === '—'" class="field-value-empty">Not provided</span>
          </div>

          <div class="field-item">
            <div class="field-title-line">
              <span class="field-label">Organization</span>
              <span class="org-sub-id font-mono">Org #{{ selectedConversation.org_id || 0 }}</span>
            </div>
            <span class="field-value">{{ selectedConversation.org_name || 'Certiplate' }}</span>
          </div>

          <div class="field-item">
            <div class="field-title-line">
              <span class="field-label">Thread Stats</span>
            </div>
            <div class="stats-row">
              <div class="stat-cell">
                <span class="stat-count">{{ messages.length }}</span>
                <span class="stat-label">Messages</span>
              </div>
              <div class="stat-cell">
                <span class="stat-count">{{ formatTime(selectedConversation.last_message_time) || 'Recent' }}</span>
                <span class="stat-label">Last Active</span>
              </div>
            </div>
          </div>
        </div>

      </aside>

    </div>

  </div>

  <!-- =========================================================================
       4. SEND CHAT INVITE MODAL POPUP (Exact Ticket-Style Modal)
       ========================================================================= -->
  <div class="vss-modal" [ngClass]="{'show': showSendInviteModal}" id="vssSendInviteModal">
    <div class="vss-modal-dialog">
      <div class="vss-modal-content">

        <!-- Modal Header -->
        <div class="vss-modal-header">
          <h3 class="vss-modal-title">
            <i class="fa fa-user-plus" style="color: #059669; margin-right: 6px;"></i>
            Invite Support Member
          </h3>
          <button type="button" class="vss-close" (click)="closeSendInviteModal()">&times;</button>
        </div>

        <!-- Modal Body (Form) -->
        <div class="vss-modal-body">
          <p style="font-size: 13px; color: #64748b; margin-top: 0; margin-bottom: 16px; line-height: 1.5;">
            Invite a team member by email to grant them access to reply to user chats and provide live support.
          </p>

          <form (ngSubmit)="submitSendInvite()" #sendInviteForm="ngForm" novalidate>
            
            <!-- Email Address Field -->
            <div class="form-group" [ngClass]="{'has-error': inviteErrors.email}">
              <label for="inviteEmail" class="form-label">
                Support Member Email Address <span class="text-danger">*</span>
              </label>
              <input 
                type="email" 
                id="inviteEmail" 
                name="inviteEmail" 
                class="form-controls"
                placeholder="e.g. support.agent@certiplate.com"
                [(ngModel)]="inviteForm.email" 
                required />
              <div *ngIf="inviteErrors.email" class="error-message">
                {{ inviteErrors.email }}
              </div>
            </div>

            <!-- Invitation Message Field -->
            <div class="form-group" [ngClass]="{'has-error': inviteErrors.message}">
              <label for="inviteMessage" class="form-label">
                Invitation Message <span class="text-danger">*</span>
              </label>
              <textarea 
                id="inviteMessage" 
                name="inviteMessage" 
                rows="4" 
                class="form-controls"
                placeholder="Enter invitation note or instructions for the support member..."
                [(ngModel)]="inviteForm.message" 
                required>
              </textarea>
              <div *ngIf="inviteErrors.message" class="error-message">
                {{ inviteErrors.message }}
              </div>
            </div>

            <!-- Modal Footer Buttons -->
            <div class="vss-modal-footer">
              <button 
                type="button" 
                class="fd-btn-modal-cancel" 
                (click)="closeSendInviteModal()">
                Cancel
              </button>
              
              <button 
                type="submit" 
                class="fd-btn-modal-submit" 
                [disabled]="isSendingInvite">
                <i *ngIf="!isSendingInvite" class="fa fa-paper-plane"></i>
                <i *ngIf="isSendingInvite" class="fa fa-circle-o-notch fa-spin"></i>
                <span>{{ isSendingInvite ? 'Sending...' : 'Send Invitation' }}</span>
              </button>
            </div>

          </form>
        </div>

      </div>
    </div>
  </div>

</div>
</ng-container>
